import { ReactNode, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { ErrorBoundary } from "components/atoms/ErrorBoundary";
import { useAuth0 } from "@auth0/auth0-react";
import { getPendingShopifyLink } from "utils/shopifyPendingLink";
import { useDispatch, useSelector } from "react-redux";
import { loginThunk } from "store/authSlice";
import { RootState } from "store";
import { fetchSalesAccountsThunk } from "store/salesAccountSlice/index.slice";
import { IUser } from "models/user/user.model";
import { fetchUsersThunk } from "store/usersSlice/index.slice";
import { isUserAdmin } from "utils/isUserAdmin";
import {
  isUserAdminForSalesAccounts,
  isUserSalesPeopleForSalesAccounts,
  isUserCustomerWithAccessToChildAccounts,
} from "utils/isUserAdminForSalesAccounts";
import { fetchBulkShipmentProfilesThunk } from "store/bulkShipmentProfilesSlice/index.slice";
import {
  fetchCompanyAddressesThunk,
  fetchCompanyContactsThunk,
  fetchSettingsThunk,
} from "store/settingsSlice/index.slice";
import { fetchShippingWhitelistsThunk } from "store/shippingWhitelistsSlice/index.slice";
import { fetchCurrentLabelJobsThunk } from "store/currentLabelJobsSlice/index.slice";
import { fetchNotificationsThunk } from "store/notificationsSlice/index.slice";
import { PusherData } from "components/organisms/PusherData";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { Center } from "components/atoms/Center";
import { Error } from "components/atoms/Error";
import { getAppLoadingState } from "./LayoutAuthenticated.utils";
import { fetchIntegrationsThunk } from "store/integrationsSlice/index.slice";
import { fetchVirtualWarehousesThunk } from "store/warehousesSlice/index.slice";
import TopBanner from "components/atoms/TopBanner";
import { CarrierOutageBanners } from "components/organisms/CarrierOutageBanner";
import { fetchSingularSalesAccountThunk } from "store/salesAccountSlice/index.slice";
import { identifyAnalyticsUser } from "utils/gtag";

export const LayoutAuthenticated = ({ children }: { children?: ReactNode }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const {
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading: isLoadingUser,
    isAuthenticated,
  } = useAuth0<IUser>();

  const {
    accessToken,
    salesAccountID,
    alertType,
    accountStatus,
    currentUserID,
    loadingStatus: authLoadingStatus,
  } = useSelector((state: RootState) => {
    return state.authSlice;
  });

  const isOnStop = alertType === "onstop";
  // An inactive account shows the grey banner in place of the red on-stop one.
  const isNotActive = !!accountStatus && accountStatus !== "A";

  const { loadingStatus: usersLoadingStatus } = useSelector(
    (state: RootState) => {
      return state.usersSlice;
    },
  );

  const { loadingStatus: customersLoadingStatus } = useSelector(
    (state: RootState) => {
      return state.salesAccountSlice;
    },
  );

  const { loadingStatus: bulkShipmentProfilesLoadingStatus } = useSelector(
    (state: RootState) => {
      return state.bulkShipmentProfilesSlice;
    },
  );

  const { loadingBaseStatus: settingsLoadingStatus } = useSelector(
    (state: RootState) => {
      return state.settingsSlice;
    },
  );

  const { loadingStatus: virtualWarehousesLoadingStatus } = useSelector(
    (state: RootState) => {
      return state.virtualWarehousesSlice;
    },
  );

  const isAdmin = isUserAdmin(user);
  const isAdminForSalesAccounts = isUserAdminForSalesAccounts(user);
  const isUserSalesPeople = isUserSalesPeopleForSalesAccounts(user);
  const isUserCustomerWithAccessToChilds =
    isUserCustomerWithAccessToChildAccounts(user);

  const isUserRoleDefined =
    (user && user["https://connexx.com/roles"].length > 0) || false;

  // The dashboard layout fills the window, so tell it how tall the banners
  // above it are; otherwise the whole window scrolls and the banners scroll
  // out of view.
  const topBannersRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const banners = topBannersRef.current;
    if (!banners || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const update = () =>
      root.style.setProperty(
        "--connexx-top-banners-height",
        `${banners.offsetHeight}px`,
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(banners);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--connexx-top-banners-height");
    };
  }, [isLoadingUser, isUserRoleDefined]);

  useEffect(() => {
    if (user && getAccessTokenSilently) {
      getAccessTokenSilently({
        audience: process.env.NEXT_PUBLIC_AUDIENCE,
      }).then((resp) => {
        window["accessToken"] = resp;
        dispatch(loginThunk(user, resp));
      });
    }
  }, [user, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated && !isLoadingUser) {
      loginWithRedirect();
    }
  }, [isLoadingUser, user]);

  // Refreshes token every 50 minutes to ensure it doesn't expire during a session (default token expiry is 60 minutes in Auth0)
  useEffect(() => {
    if (!isAuthenticated) return;
  
    const refreshToken = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: process.env.NEXT_PUBLIC_AUDIENCE,
        });
        window["accessToken"] = token;
      } catch (e) {
        console.error("Failed to refresh token:", e);
      }
    };
  
    refreshToken();
  
    const interval = setInterval(refreshToken, 50 * 60 * 1000);
  
    return () => clearInterval(interval);
  }, [getAccessTokenSilently, isAuthenticated, user]);

  // Poll notifications every 10 minutes so the bell badge stays current for
  // logged-in customers without a page reload.
  useEffect(() => {
    if (!isAuthenticated || !accessToken || isAdmin) return;

    const notificationsPoll = setInterval(() => {
      dispatch(fetchNotificationsThunk());
    }, 10 * 60 * 1000);

    return () => clearInterval(notificationsPoll);
  }, [isAuthenticated, accessToken, isAdmin]);
  

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    // The three sales-account roles need both the customer data and the admin
    // data, so each dispatch list must run at most once regardless of how many
    // role conditions match; dispatching a thunk twice is two network calls.
    const hasSalesAccountRole =
      isAdminForSalesAccounts ||
      isUserSalesPeople ||
      isUserCustomerWithAccessToChilds;

    // Fetch customer data
    if (!isAdmin || hasSalesAccountRole) {
      dispatch(fetchBulkShipmentProfilesThunk());
      dispatch(fetchSettingsThunk());
      dispatch(fetchCompanyAddressesThunk());
      dispatch(fetchCompanyContactsThunk());
      dispatch(fetchShippingWhitelistsThunk());
      dispatch(fetchCurrentLabelJobsThunk());
      dispatch(fetchIntegrationsThunk());
      dispatch(fetchVirtualWarehousesThunk());
      dispatch(fetchSingularSalesAccountThunk());
    }
    if (!isAdmin) {
      dispatch(fetchNotificationsThunk());
    }
    // Fetch admin only data
    if (isAdmin || hasSalesAccountRole) {
      dispatch(fetchSalesAccountsThunk());
      dispatch(fetchUsersThunk());
    }
  }, [isAuthenticated, accessToken, salesAccountID]);

  // Identify the user in Google Analytics once their account/user id is loaded.
  useEffect(() => {
    if (salesAccountID == null && currentUserID == null) return;
    identifyAnalyticsUser({ salesAccountID, currentUserID });
  }, [salesAccountID, currentUserID]);

  // Resume an in-flight Shopify App Store install. The public link page
  // stashes the single-use link token before authentication because the Auth0
  // login redirect always lands on the site root, dropping any return URL.
  // Once the session is ready, send the user back to the confirm page.
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    if (router.pathname === "/settings/integrations/shopify-connect") return;
    if (getPendingShopifyLink()) {
      router.replace("/settings/integrations/shopify-connect");
    }
  }, [isAuthenticated, accessToken, router.pathname]);

  const loadingStatus = getAppLoadingState({
    isAdmin,
    authLoadingStatus,
    virtualWarehousesLoadingStatus,
    usersLoadingStatus,
    settingsLoadingStatus,
    customersLoadingStatus,
    bulkShipmentProfilesLoadingStatus,
  });

  return (
    <ErrorBoundary
      FallbackComponent={({ resetErrorBoundary }) => {
        return (
          <div className="flex items-center w-screen h-screen">
            <Center>
              <Error onClick={resetErrorBoundary} />
            </Center>
          </div>
        );
      }}
    >
      {isLoadingUser ? null : isUserRoleDefined ? (
        <>
          <div ref={topBannersRef}>
            {isNotActive ? (
              <TopBanner variant="inactive" />
            ) : isOnStop ? (
              <TopBanner />
            ) : null}
            <CarrierOutageBanners />
          </div>
          {loadingStatus === ApiLoadingStatus.FAILED ? (
            <div className="flex items-center w-screen h-screen">
              <Center>
                <Error
                  buttonLabel="Refresh"
                  onClick={() => {
                    location.reload();
                  }}
                />
              </Center>
            </div>
          ) : loadingStatus === ApiLoadingStatus.LOADING ? null : (
            <PusherData>{children}</PusherData>
          )}
        </>
      ) : null}
    </ErrorBoundary>
  );
};
